import { type EventDataType, tracker } from '@ixigo/analytics';

// we're using any in the following code
// although it's not recommended but sometime to make things generic for consumer of these utility
// using sparingly is fine until we find a better solution

const baseTrackEvent = tracker.track;

type Tracker = (eventName: string, eventData?: Partial<EventDataType>) => void;

export type TrackingContext = {
	/**
	 * if not given fallback to passed  track function to createTracker or default track event function
	 */
	track?: Tracker;
	getCommonAttributes?: () => Partial<Parameters<Tracker>[1]>;
};

/**
 * trackMap is a record of event names and their corresponding functions
 * if the function returns undefined, the event will not be tracked
 */
export type TrackMap = Record<
	string,
	// biome-ignore lint/suspicious/noExplicitAny: args of individual event functions are not known
	(...args: any[]) =>
		| {
				eventName: string;
				eventData?: NonNullable<Parameters<Tracker>[1]>;
		  }
		| undefined
>;

export function createTracker<T extends TrackMap>({
	trackMap,
	// TODO: EventDataType should accept undefined for property value as well bcoz clevertap ignores undefined values
	// this would help in keeping code clean and avoid conditionals in event data
	track: defaultTrack = baseTrackEvent as Tracker,
	getCommonAttributes: defaultGetCommonAttributes = () => ({}),
}: {
	trackMap: T;
} & Partial<TrackingContext>) {
	const defaultContext: () => TrackingContext = () => ({
		track: defaultTrack,
		getCommonAttributes: defaultGetCommonAttributes,
	});

	const context = { current: defaultContext };

	const entries = Object.entries(trackMap);

	// biome-ignore lint/suspicious/noExplicitAny: args of individual event functions are not known
	const tracker: Record<string, (...args: any[]) => void> = {};

	entries.forEach(([key, value]) => {
		tracker[key] = (...args: Parameters<T[typeof key]>) => {
			const {
				track = defaultTrack,
				getCommonAttributes = defaultGetCommonAttributes,
			} = context.current();
			const event = value(...args);
			if (!event) {
				return;
			}
			track(event.eventName, {
				...getCommonAttributes(),
				...event.eventData,
			});
		};
	});
	return {
		/**
		 * allows to track any events with common attributes
		 */
		track: (eventName: string, eventData?: Parameters<Tracker>[1]) => {
			const {
				track = defaultTrack,
				getCommonAttributes = defaultGetCommonAttributes,
			} = context.current();
			const commonAttributes = getCommonAttributes();
			track(eventName, { ...commonAttributes, ...eventData });
		},
		/**
		 * allows to track events defined in trackMap
		 * it also attaches the common attributes to the event data
		 */
		tracker: tracker as {
			[K in keyof T]: (...args: Parameters<T[K]>) => void;
		},
		/**
		 * allows to set the context for the tracker
		 * with trackEvent fucntion and common attributes in a funnel
		 */
		setContext: (contextGetter?: () => TrackingContext) => {
			context.current = contextGetter || defaultContext;
		},
		/**
		 * allows to get the context for the tracker
		 */
		getContext: () => context.current(),
	};
}
